import { useState, useEffect } from "react";
import { ChevronUp, Shield, Mail, ExternalLink } from "lucide-react";

const GolfSkinsPrivacy = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="golfskins-privacy-page">
      <main className="pp-main" data-testid="golfskins-privacy-content">
        <article className="pp-article">
          {/* Header */}
          <header className="pp-header" data-testid="golfskins-privacy-header">
            <div className="pp-header-icon">
              <Shield size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Privacy Policy for Golf Skins</h1>
            <p className="pp-last-updated">Last updated: April 14, 2026</p>
          </header>

          {/* 1. Introduction */}
          <section id="introduction" className="pp-section" data-testid="section-gs-introduction">
            <h2 className="pp-section-heading">1. Introduction</h2>
            <p>
              This Privacy Policy describes how Golf Skins ("we," "us," or "our"), operated by{" "}
              <strong>Radhesh Gowd</strong> as an individual developer based in Andhra Pradesh,
              India, collects, uses, and shares information when you use the Golf Skins mobile
              application (the "App"). Golf Skins is a golf bet tracking and scorecard
              application available on the Apple App Store.
            </p>
            <p>
              We respect your privacy and are committed to protecting the personal information
              you share with us. By using the App, you agree to the collection and use of
              information in accordance with this policy.
            </p>
          </section>

          {/* 2. Information We Collect */}
          <section id="information-collected" className="pp-section" data-testid="section-gs-info-collected">
            <h2 className="pp-section-heading">2. Information We Collect</h2>

            <h3 className="pp-subsection-heading">2.1 Information You Provide</h3>

            <p>
              <strong>Display name.</strong> When you first use the App, you are asked to
              provide a display name. This name is used to identify you on scorecards, in
              player groups, and in game history. You can change your display name at any time
              from the Settings screen.
            </p>
            <p>
              <strong>Authentication information.</strong> If you choose to sign in with Apple,
              Apple shares your Apple ID (a unique identifier), your email address (if you
              consent), and optionally your full name with us. This information is stored in
              our authentication system to create your account. We do not receive your Apple ID
              password or any other credentials.
            </p>
            <p>
              <strong>Guest mode.</strong> You may use the App without creating an account by
              selecting "Continue as Guest." In guest mode, we do not collect your email
              address or Apple ID. A locally-generated anonymous identifier is stored on your
              device to associate your game data with your guest session.
            </p>
            <p>
              <strong>Game data.</strong> When you use the App, we collect the game-related
              information you enter, including:
            </p>
            <ul className="pp-list">
              <li>Course name and par information</li>
              <li>Player names and handicaps for the players in your games</li>
              <li>Scores for each hole</li>
              <li>Bet amounts and game types (Skins, Nassau, Wolf, Best Ball)</li>
              <li>
                Game results and running balances in The Tab (running ledger feature)
              </li>
            </ul>

            <h3 className="pp-subsection-heading">2.2 Information Collected Automatically</h3>

            <p>
              <strong>Usage analytics.</strong> We use PostHog, a product analytics service, to
              understand how users interact with the App so we can improve it. PostHog collects
              anonymized event data about feature usage, including:
            </p>
            <ul className="pp-list">
              <li>When a game is started (including the game type, such as Wolf)</li>
              <li>When a game is completed</li>
              <li>When game results are shared</li>
              <li>When a Tab group is created</li>
            </ul>
            <p>
              PostHog does not receive your display name, email address, game scores, or bet
              amounts. PostHog receives an anonymous user identifier, device type, app version,
              operating system version, and the timestamp of each event. You can opt out of
              analytics collection by disabling "Usage Analytics" in the Settings screen.
            </p>

            <p>
              <strong>Advertising attribution (Meta).</strong> We use the Meta SDK (Facebook
              App Events) to measure the effectiveness of our advertising campaigns and to
              attribute app installs to specific ads. The Meta SDK collects your Apple
              Advertising Identifier (IDFA) only if you grant permission through the iOS App
              Tracking Transparency prompt, as well as device information such as operating
              system version, device model, and country. Meta may use this information to serve
              you personalized ads on Meta platforms (Facebook, Instagram) and to create
              lookalike audiences for our advertising campaigns.
            </p>
            <p>You can opt out of tracking at any time by:</p>
            <ul className="pp-list">
              <li>Declining the App Tracking Transparency prompt on first launch, or</li>
              <li>
                Going to iOS Settings &gt; Privacy &amp; Security &gt; Tracking and disabling
                Golf Skins
              </li>
            </ul>
            <p>
              When tracking is disabled, Meta cannot associate your device with your App Store
              installs or in-app events for advertising purposes.
            </p>

            <p>
              <strong>Subscription and purchase information.</strong> We use RevenueCat to
              process in-app subscriptions and purchases. When you make a purchase or start a
              trial, RevenueCat receives your anonymous App User ID (a RevenueCat-generated
              identifier, not your Apple ID or email), transaction receipts from Apple, and
              product information. Apple processes the actual payment; we never see your credit
              card number, Apple Pay details, or billing address. RevenueCat stores your
              entitlement status (whether you have an active subscription or lifetime purchase)
              so we can unlock premium features across your devices.
            </p>

            <h3 className="pp-subsection-heading">2.3 Information We Do Not Collect</h3>
            <p>We do not collect:</p>
            <ul className="pp-list pp-list-cross">
              <li>Location data</li>
              <li>Photos, camera, or microphone access</li>
              <li>Contacts or address book</li>
              <li>Health or fitness data</li>
              <li>Browsing history outside the App</li>
              <li>
                Any financial information beyond what Apple shares with us for verifying in-app
                purchases
              </li>
            </ul>
          </section>

          {/* 3. How We Use Your Information */}
          <section id="how-we-use" className="pp-section" data-testid="section-gs-how-we-use">
            <h2 className="pp-section-heading">3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="pp-list">
              <li>
                Provide the core functionality of the App, including scoring, bet tracking,
                game history, and player groups
              </li>
              <li>
                Sync your games across devices if you are signed in with Apple
              </li>
              <li>
                Enable real-time multiplayer ("Live Scoring") so everyone in your foursome sees
                scores update simultaneously
              </li>
              <li>
                Send you product updates and notifications about features you may care about
              </li>
              <li>Process and manage your subscriptions and purchases</li>
              <li>
                Measure the effectiveness of our advertising and improve our marketing
              </li>
              <li>
                Understand how users interact with the App so we can fix bugs and build new
                features
              </li>
              <li>Comply with legal obligations and enforce our terms</li>
            </ul>
          </section>

          {/* 4. How We Share Your Information */}
          <section id="how-we-share" className="pp-section" data-testid="section-gs-how-we-share">
            <h2 className="pp-section-heading">4. How We Share Your Information</h2>
            <p>
              <strong>We do not sell your personal information.</strong> We share information
              only in the following circumstances:
            </p>

            <h3 className="pp-subsection-heading">4.1 Service Providers</h3>
            <p>
              We share information with third-party service providers who help us operate the
              App:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Supabase</strong> — our backend database and authentication provider.
                Supabase stores your account information, game data, and the real-time
                synchronization infrastructure for Live Scoring. Supabase processes data in the
                United States.{" "}
                <a href="https://supabase.com/privacy" target="_blank" rel="noopener noreferrer" className="pp-link" data-testid="supabase-privacy-link">
                  Supabase Privacy Policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>Apple</strong> — authentication via Sign in with Apple, payment
                processing for subscriptions and in-app purchases, and app analytics via the
                App Store.{" "}
                <a href="https://www.apple.com/privacy/" target="_blank" rel="noopener noreferrer" className="pp-link" data-testid="apple-privacy-link">
                  Apple Privacy Policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>PostHog</strong> — product analytics. PostHog processes data in the
                United States.{" "}
                <a href="https://posthog.com/privacy" target="_blank" rel="noopener noreferrer" className="pp-link" data-testid="posthog-privacy-link">
                  PostHog Privacy Policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>RevenueCat</strong> — subscription management. RevenueCat processes
                data in the United States.{" "}
                <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer" className="pp-link" data-testid="revenuecat-gs-privacy-link">
                  RevenueCat Privacy Policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>Meta (Facebook)</strong> — advertising attribution and ad measurement,
                only with your consent via the App Tracking Transparency prompt.{" "}
                <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="pp-link" data-testid="meta-privacy-link">
                  Meta Privacy Policy
                  <ExternalLink size={12} className="pp-external-icon" />
                </a>
              </li>
              <li>
                <strong>GolfCourseAPI</strong> — we query golf course data (course names, pars)
                from a third-party API at runtime. When you search for a course, your device
                makes a request to this API, which may log your IP address in its server logs.
                We do not send your personal information, account data, or game data to this
                service.
              </li>
            </ul>

            <h3 className="pp-subsection-heading">4.2 Other Users in Your Games</h3>
            <p>
              When you use Live Scoring, the scores you enter are visible in real time to other
              players you have invited to join your game. Your display name is visible to those
              players during the game.
            </p>

            <h3 className="pp-subsection-heading">4.3 Legal Requirements</h3>
            <p>
              We may disclose your information if required to do so by law or in response to a
              valid legal request from government authorities.
            </p>

            <h3 className="pp-subsection-heading">4.4 Business Transfers</h3>
            <p>
              If we are involved in a merger, acquisition, or sale of assets, your information
              may be transferred as part of that transaction. We will notify you before your
              information is transferred and becomes subject to a different privacy policy.
            </p>

            <h3 className="pp-subsection-heading">4.5 Meta (Facebook) Pixel and SDK</h3>
            <p>
              We use the Meta SDK and Meta Pixel for marketing analytics and ad campaign
              measurement. When you grant App Tracking Transparency permission on iOS, this SDK
              may collect your device's advertising identifier (IDFA), app install and event
              data, and information about how you interact with our marketing campaigns. This
              data is used to measure the effectiveness of our advertising and to deliver more
              relevant ads.
            </p>
            <p>
              If you do not grant ATT permission, the Meta SDK does not collect your IDFA. You
              can manage tracking permissions at any time in iOS Settings under Privacy &amp;
              Security &gt; Tracking. For more information about Meta's data practices, see{" "}
              <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noopener noreferrer" className="pp-link" data-testid="meta-privacy-link-2">
                Meta's Privacy Policy
                <ExternalLink size={12} className="pp-external-icon" />
              </a>
              .
            </p>
          </section>

          {/* 5. Data Retention */}
          <section id="data-retention" className="pp-section" data-testid="section-gs-data-retention">
            <h2 className="pp-section-heading">5. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to
              provide the App's services. You can delete your account at any time from the
              Settings screen ("Delete Account" in the Danger Zone section). When you delete
              your account, all of your personal information, game history, players, and Tab
              balances are permanently deleted from our database within 24 hours. Anonymous
              usage analytics in PostHog may be retained for up to 12 months for aggregated
              reporting.
            </p>
            <p>
              RevenueCat and Apple retain transaction records as required by law and for tax
              purposes, typically 7 years.
            </p>
          </section>

          {/* 6. Your Privacy Rights */}
          <section id="privacy-rights" className="pp-section" data-testid="section-gs-privacy-rights">
            <h2 className="pp-section-heading">6. Your Privacy Rights</h2>
            <p>
              Depending on your jurisdiction, you may have the following rights:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Access</strong> — request a copy of the personal information we hold
                about you
              </li>
              <li>
                <strong>Correction</strong> — request that we correct any inaccurate
                information
              </li>
              <li>
                <strong>Deletion</strong> — request that we delete your personal information
                (you can also do this yourself via the Delete Account feature in the App)
              </li>
              <li>
                <strong>Portability</strong> — request that we provide your information in a
                machine-readable format
              </li>
              <li>
                <strong>Objection</strong> — object to our processing of your information in
                certain circumstances
              </li>
              <li>
                <strong>Withdrawal of consent</strong> — withdraw your consent where we rely on
                consent to process your information
              </li>
            </ul>
            <p>
              To exercise any of these rights, contact us at{" "}
              <a href="mailto:support@radships.com" className="pp-link" data-testid="gs-rights-email">
                support@radships.com
              </a>
              . We will respond within 30 days.
            </p>

            <h3 className="pp-subsection-heading">
              6.1 Users in the European Union and United Kingdom (GDPR / UK GDPR)
            </h3>
            <p>
              We process personal information on the following legal bases: performance of a
              contract (to provide the App), legitimate interests (to improve the App and
              prevent fraud), consent (for optional tracking and analytics), and legal
              obligation.
            </p>
            <p>
              If you are in the EU or UK, you have the right to lodge a complaint with your
              local data protection authority.
            </p>

            <h3 className="pp-subsection-heading">
              6.2 Users in California (CCPA / CPRA)
            </h3>
            <p>
              California residents have the right to know what personal information we collect,
              to request deletion, to opt out of the "sale" or "sharing" of personal
              information, and to not be discriminated against for exercising these rights. We
              do not sell personal information in the traditional sense, but the use of Meta SDK
              for advertising attribution may be considered "sharing" under California law. You
              can opt out by declining the App Tracking Transparency prompt.
            </p>

            <h3 className="pp-subsection-heading">
              6.3 Users in India (DPDP Act)
            </h3>
            <p>
              As an Indian developer, we comply with the Digital Personal Data Protection Act,
              2023. Indian users have the right to access, correct, and erase their personal
              information, and to withdraw consent. We process personal information based on
              your consent, which you provide by using the App and, where applicable, accepting
              the App Tracking Transparency prompt.
            </p>
          </section>

          {/* 7. Children's Privacy */}
          <section id="children" className="pp-section" data-testid="section-gs-children">
            <h2 className="pp-section-heading">7. Children's Privacy</h2>
            <p>
              Golf Skins is rated 12+ on the App Store and is not intended for children under
              13. We do not knowingly collect personal information from children under 13. If we
              learn that we have collected personal information from a child under 13, we will
              delete it promptly. If you believe a child has provided us with personal
              information, contact us at{" "}
              <a href="mailto:support@radships.com" className="pp-link" data-testid="gs-children-email">
                support@radships.com
              </a>
              .
            </p>
          </section>

          {/* 8. Data Security */}
          <section id="data-security" className="pp-section" data-testid="section-gs-security">
            <h2 className="pp-section-heading">8. Data Security</h2>
            <p>
              We implement reasonable technical and organizational measures to protect your
              information, including encryption in transit (HTTPS/TLS) for all data transmitted
              between the App and our servers. However, no method of electronic transmission or
              storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* 9. International Data Transfers */}
          <section id="international" className="pp-section" data-testid="section-gs-international">
            <h2 className="pp-section-heading">9. International Data Transfers</h2>
            <p>
              Golf Skins is operated from India, but our service providers (Supabase, PostHog,
              RevenueCat, Meta) process data primarily in the United States. By using the App,
              you consent to the transfer of your information to these countries, which may have
              different data protection laws than your country of residence.
            </p>
          </section>

          {/* 10. Changes */}
          <section id="changes" className="pp-section" data-testid="section-gs-changes">
            <h2 className="pp-section-heading">10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of
              material changes by updating the "Last updated" date at the top of this policy
              and, where appropriate, providing an in-app notification. Your continued use of
              the App after changes are posted constitutes your acceptance of the updated
              policy.
            </p>
          </section>

          {/* 11. Contact */}
          <section id="contact" className="pp-section" data-testid="section-gs-contact">
            <h2 className="pp-section-heading">11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or our privacy practices,
              please contact us at:
            </p>
            <div className="pp-contact-card" data-testid="gs-contact-card">
              <p className="pp-contact-name">Radhesh Gowd</p>
              <p className="pp-contact-location">Individual Developer</p>
              <p className="pp-contact-location">Andhra Pradesh, India</p>
              <a
                href="mailto:support@radships.com"
                className="pp-contact-email"
                data-testid="gs-contact-email-link"
              >
                <Mail size={16} />
                support@radships.com
              </a>
            </div>
          </section>

          {/* Footer */}
          <footer className="pp-footer" data-testid="golfskins-privacy-footer">
            <p>&copy; {new Date().getFullYear()} Golf Skins. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {/* Back to top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="gs-back-to-top-button"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default GolfSkinsPrivacy;
